import { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";

interface Props {
    onCapture: (blob: Blob) => Promise<void> | void;
}

export default function FaceLoginCamera({ onCapture }: Props) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [ready, setReady] = useState(false);
    const [detected, setDetected] = useState(false);
    const [status, setStatus] = useState("Initializing camera...");
    const [frozen, setFrozen] = useState(false);
    const [canRetry, setCanRetry] = useState(false);
    const [cameraOff, setCameraOff] = useState(false);
    const stableDetections = useRef(0);

    // 🧠 Load face model
    useEffect(() => {
        async function loadModels() {
            try {
                await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
                console.log("✅ tiny_face_detector model loaded");
            } catch (err) {
                console.error("❌ Model load error:", err);
                setStatus("Error loading model");
            }
        }
        loadModels();
    }, []);

    // 🎥 Start camera
    useEffect(() => {
        if (cameraOff) return;

        async function startCamera() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                    setReady(true);
                    setStatus("Align your face within the oval");
                }
            } catch (err) {
                console.error("Camera error:", err);
                setStatus("Unable to access camera");
            }
        }
        startCamera();

        return () => {
            if (videoRef.current?.srcObject instanceof MediaStream) {
                videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
            }
        };
    }, [cameraOff]);

    // 🔍 Detection loop
    useEffect(() => {
        let stop = false;

        async function detectLoop() {
            if (!videoRef.current || frozen || cameraOff) return;

            try {
                if (videoRef.current.videoWidth === 0) {
                    requestAnimationFrame(detectLoop);
                    return;
                }

                const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.2 });
                const result = await faceapi.detectSingleFace(videoRef.current, options);

                if (result) {
                    stableDetections.current += 1;

                    if (!detected) {
                        setDetected(true);
                        setStatus("🟢 Face detected — hold still");
                    }

                    // ✅ As soon as a stable face is detected, capture once
                    if (stableDetections.current >= 2) {
                        await captureFrame();
                        stop = true;
                    }
                } else {
                    if (detected) {
                        setDetected(false);
                        setStatus("No face detected — align within oval");
                    }
                    stableDetections.current = 0;
                }
            } catch (err) {
                console.error("Detection error:", err);
            }

            if (!stop) requestAnimationFrame(detectLoop);
        }

        async function captureFrame() {
            if (!videoRef.current) return;
            setFrozen(true);
            setStatus("📸 Capturing image...");

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d")!;
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            ctx.drawImage(videoRef.current, 0, 0);

            canvas.toBlob(async (blob) => {
                if (blob) {
                    setStatus("🔍 Validating face...");
                    try {
                        await onCapture(blob); // wait for backend verification
                        setStatus("✅ Face sent successfully!");
                    } catch (err) {
                        console.error("onCapture failed:", err);
                        setStatus("⚠️ Face verification failed");
                    } finally {
                        setCanRetry(true); // allow retry only after backend finishes
                    }
                } else {
                    setStatus("⚠️ Failed to capture image");
                    setCanRetry(true);
                }
            }, "image/jpeg", 0.9);
        }

        if (ready) detectLoop();
        return () => {
            stop = true;
        };
    }, [ready, detected, frozen, onCapture, cameraOff]);

    // 🔁 Try Again handler
    const handleRetry = () => {
        setFrozen(false);
        setDetected(false);
        setCanRetry(false);
        stableDetections.current = 0;
        setStatus("Align your face within the oval");
        console.log("🔁 Retry started");
    };

    // 🔒 Turn Off Camera handler
    const handleTurnOffCamera = () => {
        if (videoRef.current?.srcObject instanceof MediaStream) {
            videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
        }
        setCameraOff(true);
        setReady(false);
        setFrozen(true);
        setStatus("📴 Camera turned off");
        console.log("🔒 Camera turned off manually");
    };

    return (
        <div className="relative w-full max-w-sm aspect-square mx-auto overflow-hidden rounded-lg shadow-lg bg-black">
            {/* Video feed */}
            <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`object-cover w-full h-full transition-opacity duration-500 ${frozen ? "opacity-60" : "opacity-100"
                    }`}
            />

            {/* Oval overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                    className={`border-4 rounded-full w-64 h-80 transition-colors duration-500 ${detected ? "border-green-400" : "border-white/70"
                        }`}
                    style={{
                        boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                        clipPath: "ellipse(50% 60% at 50% 50%)",
                    }}
                ></div>
            </div>

            {/* Status label + buttons */}
            <div className="absolute bottom-3 w-full text-center flex flex-col items-center">
                <span
                    className={`text-sm font-medium px-3 py-1 rounded-md transition-all duration-500 mb-2 ${status.includes("✅")
                            ? "bg-green-600 text-white"
                            : status.includes("⚠️")
                                ? "bg-yellow-600 text-white"
                                : status.includes("📴")
                                    ? "bg-gray-700 text-gray-200"
                                    : "bg-black/60 text-white"
                        }`}
                >
                    {status}
                </span>

                {/* Retry button */}
                {canRetry && !cameraOff && (
                    <button
                        onClick={handleRetry}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs font-semibold px-3 py-1 rounded transition-all"
                    >
                        🔁 Try Again
                    </button>
                )}

                {/* Turn off button */}
                {!cameraOff && (
                    <button
                        onClick={handleTurnOffCamera}
                        className="mt-2 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded"
                    >
                        🔒 Turn Off Camera
                    </button>
                )}
            </div>
        </div>
    );
}
